import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { LegacyFile, PaginatedDocumentTypeList } from '../../../../../js/api';
import { DocumentsService, ProvidersService } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { Checkbox, CheckboxField } from '../../../components/checkbox';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { DOCUMENT_TYPES_QUERY_KEY, DOCUMENTS_QUERY_KEY } from '../../../components/documents-tab';
import { DropdownSelect } from '../../../components/dropdown-select';
import { FieldValidationErrorMessage } from '../../../components/field-validation-error-message';
import { Description, Field, Fieldset, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { WorkflowScopes } from '../../../constants/workflows';
import { parseFormDate } from '../../../utils/date';
import { pickableUploadTypes } from '../../../utils/documentTypes';
import { useAudience } from '../../../utils/workflowAccess';
import { STAFF_PROVIDER_CREDENTIALS_QUERY_KEY } from './credentials';
import {
  legacyFilesQueryKey,
  STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY,
  STAFF_PROVIDER_QUERY_KEY,
} from './queryKeys';
import 'react-datepicker/dist/react-datepicker.css';

const schema = z
  .object({
    document_type: z.string().min(1, 'Pick the document type'),
    label: z.string().trim().max(255).optional(),
    issued_on: z.date().nullable().optional(),
    expires_on: z.date().nullable().optional(),
  })
  .refine(
    (values) => !values.issued_on || !values.expires_on || values.expires_on > values.issued_on,
    { message: 'Expiration date must be after the issue date', path: ['expires_on'] }
  );
type FormValues = z.infer<typeof schema>;

const toDate = (value: string | null | undefined): Date | null => (value ? parseISO(value) : null);

/**
 * Set a legacy upload aside (staff moved it by hand or will not convert it) or restore
 * it. An ignored file leaves the pending list and the per-tab "not yet in Documents"
 * counts, which come off the clinician detail payload.
 */
export function useSetLegacyFileIgnored(providerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, ignored }: { file: LegacyFile; ignored: boolean }) =>
      ProvidersService.providersLegacyFilesIgnoreCreate({
        id: providerId,
        requestBody: { source: file.source, source_id: file.source_id, ignored },
      }),
    onSuccess: (_data, { file, ignored }) => {
      toast.success(
        ignored
          ? `${file.file_name} is ignored and will not be converted`
          : `${file.file_name} is back in the legacy uploads`
      );
      queryClient.invalidateQueries({ queryKey: legacyFilesQueryKey(providerId) });
      queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_QUERY_KEY, providerId] });
    },
    onError: (error: Error, { ignored }) =>
      handleApiError(error, {
        messagePrefix: ignored
          ? 'Error ignoring the legacy file'
          : 'Error restoring the legacy file',
      }),
  });
}

/**
 * Turn one legacy upload into a Document: staff pick the DocumentType (preselected when
 * the legacy row implies one; locked for a credential entry's file, whose entry decides
 * the type and owns the label and dates), confirm the label and dates, and the backend
 * copies the bytes into a confirmed document on the clinician's context. Ticking Ignore
 * instead sets the file aside: nothing is copied and it leaves the legacy list.
 */
export function LegacyFileConvertDialog({
  open,
  onClose,
  providerId,
  file,
  sourceLabel,
  onConverted,
}: {
  open: boolean;
  onClose: () => void;
  providerId: string;
  file: LegacyFile;
  sourceLabel: string;
  onConverted: () => void;
}) {
  const queryClient = useQueryClient();
  const audience = useAudience();
  const locked = file.document_type_locked;
  const [ignore, setIgnore] = useState(false);
  const ignoreMutation = useSetLegacyFileIgnored(providerId);

  const { data: documentTypes, isLoading: typesLoading } = useQuery({
    queryKey: [DOCUMENT_TYPES_QUERY_KEY, WorkflowScopes.Provider, audience],
    queryFn: () => DocumentsService.documentsTypesList({ contextTypes: [WorkflowScopes.Provider] }),
    select: (data: PaginatedDocumentTypeList) => data?.results ?? [],
  });
  const typeOptions = useMemo(
    () =>
      // a credential file keeps its entry's (entry-backed) type; a loose file never gets one
      pickableUploadTypes(
        documentTypes ?? [],
        audience,
        locked ? file.suggested_document_type : null
      ).map((dt) => ({ value: dt.slug, label: dt.title })),
    [documentTypes, audience, locked, file.suggested_document_type]
  );

  const { control, register, handleSubmit, setError, setValue, watch, formState } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: {
        document_type: file.suggested_document_type ?? '',
        // a profile upload without a stored name is labelled by what it is ("Medical Diploma")
        label: file.label || sourceLabel,
        issued_on: toDate(file.issue_date),
        expires_on: toDate(file.expiration_date),
      },
    });

  const convertMutation = useMutation({
    mutationFn: (values: FormValues) =>
      ProvidersService.providersLegacyFilesConvertCreate({
        id: providerId,
        requestBody: {
          source: file.source,
          source_id: file.source_id,
          document_type: values.document_type,
          label: locked ? undefined : values.label || undefined,
          issued_on: locked ? undefined : values.issued_on ? parseFormDate(values.issued_on) : null,
          expires_on: locked
            ? undefined
            : values.expires_on
              ? parseFormDate(values.expires_on)
              : null,
        },
      }),
    onSuccess: (document) => {
      toast.success(`${document.label || file.file_name} is now a document`);
      queryClient.invalidateQueries({ queryKey: legacyFilesQueryKey(providerId) });
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, providerId] });
      queryClient.invalidateQueries({
        queryKey: [STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, providerId],
      });
      // a credential file now backs its entry: the detail payload and the tab's rows
      queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_QUERY_KEY, providerId] });
      queryClient.invalidateQueries({
        queryKey: [STAFF_PROVIDER_CREDENTIALS_QUERY_KEY, providerId],
      });
      onConverted();
      onClose();
    },
    onError: (error: Error) => {
      const body = (error as { body?: Record<string, unknown> })?.body;
      if (body && typeof body === 'object') {
        Object.entries(body).forEach(([field, messages]) => {
          if (field in schema._def.schema.shape) {
            setError(field as keyof FormValues, {
              message: Array.isArray(messages) ? String(messages[0]) : String(messages),
            });
          }
        });
      }
      handleApiError(error, { messagePrefix: 'Error converting the legacy file' });
    },
  });
  const isPending = convertMutation.isPending || ignoreMutation.isPending;
  const submitLabel = ignore
    ? ignoreMutation.isPending
      ? 'Ignoring…'
      : 'Ignore'
    : convertMutation.isPending
      ? 'Converting…'
      : 'Convert';

  return (
    <Dialog open={open} onClose={onClose} size="xl">
      <DialogTitle className="tw:!text-2xl tw:!text-brand-green-0 tw:!mt-0 tw:!pt-0">
        Convert legacy upload
      </DialogTitle>
      <form
        onSubmit={(event) => {
          if (ignore) {
            // nothing to validate: the type, label and dates describe a conversion
            event.preventDefault();
            ignoreMutation.mutate({ file, ignored: true }, { onSuccess: onClose });
            return;
          }
          handleSubmit((values) => convertMutation.mutate(values))(event);
        }}
        data-testid="legacy-convert-form"
      >
        <DialogBody>
          <p className="tw:text-sm tw:text-zinc-600 tw:!mb-3 tw:min-w-0">
            <span className="tw:font-medium">{sourceLabel}</span> ·{' '}
            {/* legacy object names are long UUID-stitched strings with no spaces */}
            <span className="tw:break-all">{file.file_name}</span>.{' '}
            {ignore
              ? 'Nothing is copied: the file leaves this list and is skipped by every conversion. It can be restored from the ignored files.'
              : "The file is copied into the clinician's documents as the type below; the legacy upload is kept."}
          </p>
          <Fieldset>
            <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-3">
              <Field className="tw:sm:col-span-2">
                <CheckboxField>
                  <Checkbox
                    color="brandgreen1"
                    checked={ignore}
                    onChange={setIgnore}
                    disabled={isPending}
                    data-testid="legacy-convert-ignore"
                  />
                  <Label>Ignore this file (moved elsewhere by hand, or not to be converted)</Label>
                </CheckboxField>
              </Field>
              {!ignore && (
                <>
                  <Field className="tw:sm:col-span-2">
                    <Label>
                      Document type <span className="tw:text-stoplight-danger">*</span>
                    </Label>
                    <DropdownSelect
                      inputId="legacy-convert-type"
                      allOptions={typeOptions}
                      value={watch('document_type') || null}
                      onChange={(option) => {
                        const next = Array.isArray(option) ? (option[0] ?? null) : option;
                        setValue('document_type', next ? next.value : '', { shouldValidate: true });
                      }}
                      isMulti={false}
                      isDisabled={locked || typesLoading || isPending}
                      placeholder={typesLoading ? 'Loading types...' : 'Select a type'}
                    />
                    {locked && (
                      <Description>
                        This file belongs to a credential entry, which decides its type and owns its
                        label and dates.
                      </Description>
                    )}
                    {formState.errors.document_type && (
                      <FieldValidationErrorMessage
                        message={formState.errors.document_type.message}
                      />
                    )}
                  </Field>
                  {!locked && (
                    <>
                      <Field className="tw:sm:col-span-2">
                        <Label>Label</Label>
                        <Input
                          {...register('label')}
                          disabled={isPending}
                          invalid={!!formState.errors.label}
                          data-testid="legacy-convert-label"
                        />
                        {formState.errors.label && (
                          <FieldValidationErrorMessage message={formState.errors.label.message} />
                        )}
                      </Field>
                      <Field>
                        <Label>Issue Date</Label>
                        <Controller
                          name="issued_on"
                          control={control}
                          render={({ field }) => (
                            <ReactDatePicker
                              className="datepicker-input"
                              wrapperClassName="datepicker-wrapper"
                              selected={field.value ?? null}
                              onChange={(date) => field.onChange(date)}
                              placeholderText="Select issue date"
                              dateFormat="MM/dd/yyyy"
                              disabled={isPending}
                            />
                          )}
                        />
                      </Field>
                      <Field>
                        <Label>Expiration Date</Label>
                        <Controller
                          name="expires_on"
                          control={control}
                          render={({ field }) => (
                            <ReactDatePicker
                              className="datepicker-input"
                              wrapperClassName="datepicker-wrapper"
                              selected={field.value ?? null}
                              onChange={(date) => field.onChange(date)}
                              placeholderText="Select expiration date"
                              dateFormat="MM/dd/yyyy"
                              disabled={isPending}
                            />
                          )}
                        />
                        {formState.errors.expires_on && (
                          <FieldValidationErrorMessage
                            message={formState.errors.expires_on.message}
                          />
                        )}
                      </Field>
                    </>
                  )}
                </>
              )}
            </div>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="zinc-outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="brandgreen1"
            disabled={isPending}
            data-testid="legacy-convert-submit"
          >
            {submitLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
